import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { History, Filter, RefreshCw } from 'lucide-react';
import { formatCaracasDateTime, TIMEZONE_LABEL } from '../lib/timezone';

const EVENT_META = {
  LOGIN:         { label: 'Login',        cls: 'stage-cerrado_ganado' },
  LOGOUT:        { label: 'Logout',       cls: 'stage-contactado' },
  STATUS_CHANGE: { label: 'Cambio estado', cls: 'stage-promesa_pago' },
};

const REASON_LABEL = {
  manual:                    'Manual',
  inactivity:                'Inactividad (5 min)',
  shift_end:                 'Fin de jornada',
  pause_exceeded:            'Exceso de pausa',
  lunch_overrun:             'No regresó de comida',
  admin_reactivate:          'Reactivación por superior',
  first_login_password_set:  'Primer ingreso (password)',
  login_failed:              'Credencial inválida',
  password_reset:            'Reseteo de contraseña',
};

const EVENT_TYPES = ['', 'LOGIN', 'LOGOUT', 'STATUS_CHANGE'];

export default function AuditLogPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState('');
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit };
      if (eventType) params.event_type = eventType;
      const { data } = await api.get('/admin/audit-log', { params });
      setEvents(data.events || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [limit, eventType]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-log-page" className="contacts-page">
      <div className="contacts-toolbar">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <History size={18} /> Historial de Acceso
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
            zona horaria · {TIMEZONE_LABEL}
          </span>
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Filter size={14} />
          <select
            data-testid="audit-event-type-filter"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}
          >
            {EVENT_TYPES.map(t => (
              <option key={t || 'all'} value={t}>
                {t ? (EVENT_META[t]?.label || t) : 'Todos los eventos'}
              </option>
            ))}
          </select>
          <select
            data-testid="audit-limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}
          >
            <option value={50}>Últimos 50</option>
            <option value={100}>Últimos 100</option>
            <option value={250}>Últimos 250</option>
            <option value={500}>Últimos 500</option>
          </select>
          <button
            data-testid="audit-refresh"
            className="btn-icon"
            title="Actualizar"
            onClick={load}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="contacts-table-wrapper" style={{ marginTop: 16 }}>
        <table data-testid="audit-log-table" className="contacts-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Rol</th>
              <th>Evento</th>
              <th>Motivo</th>
              <th>Cambio de estado</th>
              <th>IP</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-muted" style={{ padding: 16 }}>Cargando historial…</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={8} className="text-muted" style={{ padding: 16 }}>Sin eventos registrados.</td></tr>
            ) : events.map(ev => {
              const em = EVENT_META[ev.event_type] || { label: ev.event_type, cls: '' };
              const reason = REASON_LABEL[ev.reason] || ev.reason || '—';
              const change = ev.event_type === 'STATUS_CHANGE'
                ? `${ev.previous_status || '—'} → ${ev.new_status || '—'}`
                : '—';
              return (
                <tr key={ev.id} data-testid={`audit-row-${ev.id}`}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatCaracasDateTime(ev.created_at)}</td>
                  <td className="td-name">{ev.user_email || '—'}</td>
                  <td>{ev.user_role || '—'}</td>
                  <td>
                    <span className={`stage-badge ${em.cls}`}>{em.label}</span>
                  </td>
                  <td>{reason}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{change}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{ev.ip_address || '—'}</td>
                  <td>{ev.actor_email || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
